/**
 * FlameRenderer — animated campfire flame for Phaser 3.
 * Intensity scales with difficulty so the fire visibly grows more dangerous.
 *
 * ═══════════════════════════════ Public API ═══════════════════════════════
 *
 *   import { FlameRenderer } from '../render/flame';
 *
 *   // In PlayScene.create():
 *   private flame!: FlameRenderer;
 *   this.flame = new FlameRenderer(this, columnX, height * 0.9);
 *
 *   // In PlayScene.layout():
 *   this.flame.setPosition(this.columnX, height * 0.9);
 *
 *   // When difficulty changes (each toasted marshmallow):
 *   this.flame.setIntensity(this.run.getDifficulty().flameIntensity);
 *
 *   // In PlayScene.update():
 *   this.flame.update(this.time.now);
 *
 *   // In scene shutdown / destroy:
 *   this.flame.destroy();
 *
 * setIntensity() accepts the raw flameIntensity value from Difficulty:
 *   60  = BASE_FLAME_INTENSITY (default start)
 *   120 = MAX_FLAME_INTENSITY  (hardest)
 * Values outside [0, 120] are clamped gracefully.
 *
 * Depth: 1 (matches the PlayScene stack: flame=1, stick=2, marshmallow=3).
 *
 * The flame origin sits at (x, y) — the campfire base position.
 * All flames extend upward (negative Y in Phaser screen coords).
 * ════════════════════════════════════════════════════════════════════════════
 */

import Phaser from 'phaser';

export class FlameRenderer {
  private readonly g: Phaser.GameObjects.Graphics;
  private px: number;
  private py: number;
  /** Raw flameIntensity from Difficulty (60 = base, up to 120). */
  private intensity = 60;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.g = scene.add.graphics().setDepth(1);
    this.px = x;
    this.py = y;
  }

  /**
   * Reposition the campfire base. Call from PlayScene.layout() on resize.
   * @param x Screen X of the flame anchor.
   * @param y Screen Y of the flame anchor.
   */
  setPosition(x: number, y: number): void {
    this.px = x;
    this.py = y;
  }

  /**
   * Update flame intensity from the current difficulty.
   * Call once per completed marshmallow (or whenever getDifficulty() changes).
   * @param flameIntensity Raw value from Difficulty.flameIntensity (60..120).
   */
  setIntensity(flameIntensity: number): void {
    this.intensity = Math.max(0, flameIntensity);
  }

  /**
   * Redraw every frame. Call from PlayScene.update().
   * @param time scene.time.now in milliseconds.
   */
  update(time: number): void {
    const g = this.g;
    g.clear();
    g.setPosition(this.px, this.py);

    // Normalise: 0 = base difficulty (intensity=60), 1 = max (intensity=120).
    const n = Math.max(0, Math.min(1, (this.intensity - 60) / 60));
    const t = time * 0.001; // convert ms → seconds

    // ── Organic flicker via independent sine waves ──────────────────────────
    // Each layer uses a different frequency/phase so they never move in unison.
    const fa = 1 + Math.sin(t * 7.1)        * 0.12 + Math.sin(t * 3.9 + 0.40) * 0.06;
    const fb = 1 + Math.sin(t * 9.3 + 1.20) * 0.10 + Math.sin(t * 5.1)        * 0.05;
    const fc = 1 + Math.sin(t * 6.5 + 2.60) * 0.09 + Math.sin(t * 11.7 + 0.9) * 0.05;
    const fd = 1 + Math.sin(t * 4.7 + 0.70) * 0.14 + Math.sin(t * 8.3 + 1.8)  * 0.07;

    // ── Geometry — all heights scale with intensity ─────────────────────────
    const baseW  = 80 + n * 40;   // flame base width:       80 → 120 px
    const mainH  = 70 + n * 50;   // main flame body height: 70 → 120 px
    const coreH  = 42 + n * 32;   // hot core height:        42 → 74  px
    const tipH   = 18 + n * 14;   // tip height:             18 → 32  px
    const spread = 18 + n * 16;   // tongue lateral offset:  18 → 34  px

    // ── 1. Ground ember pool ───────────────────────────────────────────────
    g.fillStyle(0xc02808, 0.34);
    g.fillEllipse(0, 0, baseW * 0.92, 22);

    // ── 2. Wide outer heat shimmer ─────────────────────────────────────────
    // Very low-alpha large bloom gives an environmental heat haze.
    g.fillStyle(0xff4400, 0.07 + n * 0.05);
    g.fillEllipse(0, -(mainH * fa * 0.38), baseW * fa * 1.06, mainH * fa * 1.08);

    // ── 3. Left tongue ─────────────────────────────────────────────────────
    const lH = mainH * 0.76 * fb;
    g.fillStyle(0xd83e0e, 0.88);
    g.fillEllipse(-spread * fb, -lH * 0.52, baseW * 0.44, lH);

    // ── 4. Right tongue ────────────────────────────────────────────────────
    const rH = mainH * 0.80 * fc;
    g.fillStyle(0xcc3a0c, 0.88);
    g.fillEllipse(spread * fc, -rH * 0.52, baseW * 0.42, rH);

    // ── 5. Centre flame body (vivid orange) ────────────────────────────────
    g.fillStyle(0xff6618, 0.96);
    g.fillEllipse(0, -(mainH * fd * 0.46), baseW * 0.74 * fa, mainH * fd);

    // ── 6. Hot core (orange-yellow) ────────────────────────────────────────
    const cFlicker = 1 + Math.sin(t * 13.1 + 1.4) * 0.07;
    g.fillStyle(0xffaa1e, 0.92);
    g.fillEllipse(0, -(coreH * cFlicker * 0.47), baseW * 0.44, coreH * cFlicker);

    // ── 7. Inner core (yellow) ─────────────────────────────────────────────
    g.fillStyle(0xffdd3c, 0.90);
    g.fillEllipse(0, -coreH * 0.24, baseW * 0.27, coreH * 0.50);

    // ── 8. Tip (near-white, rapid flicker) ─────────────────────────────────
    const tFlicker = 1 + Math.sin(t * 15.7 + 2.3) * 0.11;
    const tipY     = -(mainH * fd) + tipH * 0.5;
    g.fillStyle(0xfffab8, 0.78);
    g.fillEllipse(0, tipY, baseW * 0.14, tipH * tFlicker);

    // ── 9. Base hot-spot (bright ember at fire heart) ──────────────────────
    g.fillStyle(0xff9030, 0.72);
    g.fillEllipse(0, -10, baseW * 0.36, 24);
  }

  /** Release the Phaser Graphics object. Call on scene shutdown. */
  destroy(): void {
    this.g.destroy();
  }
}
